/**
 * CaseStudies セクション。3 variants:
 *   logo_grid          — 顧客ロゴをグリッド + 業種ラベル
 *   card_with_metrics  — カード形式 (画像 + 業種 + 数値メトリクス)
 *   featured           — 1 件をフィーチャー (大きめ画像 + 詳細)
 */
import { Briefcase } from 'lucide-react';

import { ArrayField, ImageField, TextField, TextareaField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface Metric {
  value: string;
  label: string;
}
interface CaseStudy {
  companyName: string;
  industry: string;
  logoUrl: string;
  imageUrl: string;
  challenge: string;
  result: string;
  metrics: Metric[];
}
interface CaseStudiesConfig {
  title: string;
  subtitle: string;
  cases: CaseStudy[];
}

function CaseStudiesEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as CaseStudiesConfig;
  const set = (patch: Partial<CaseStudiesConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <ArrayField<CaseStudy>
        label="導入事例"
        items={cfg.cases ?? []}
        onChange={(next) => set({ cases: next })}
        emptyItem={() => ({
          companyName: '',
          industry: '',
          logoUrl: '',
          imageUrl: '',
          challenge: '',
          result: '',
          metrics: [],
        })}
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="企業名"
                value={item.companyName}
                onChange={(v) => update({ companyName: v })}
                onCommit={onCommit}
              />
              <TextField
                label="業種"
                value={item.industry}
                onChange={(v) => update({ industry: v })}
                onCommit={onCommit}
              />
            </div>
            <ImageField
              label="ロゴ画像"
              value={item.logoUrl}
              onChange={(v) => {
                update({ logoUrl: v });
                onCommit();
              }}
            />
            <ImageField
              label="メイン画像"
              value={item.imageUrl}
              onChange={(v) => {
                update({ imageUrl: v });
                onCommit();
              }}
            />
            <TextareaField
              label="課題"
              value={item.challenge}
              onChange={(v) => update({ challenge: v })}
              onCommit={onCommit}
              rows={2}
            />
            <TextareaField
              label="導入結果"
              value={item.result}
              onChange={(v) => update({ result: v })}
              onCommit={onCommit}
              rows={2}
            />
            <TextareaField
              label="メトリクス (1 行 = 数値|ラベル, 例: 40%|工数削減)"
              value={(item.metrics ?? [])
                .map((m) => `${m.value}|${m.label}`)
                .join('\n')}
              onChange={(v) => {
                const metrics: Metric[] = v
                  .split('\n')
                  .map((line) => {
                    const [val, lbl] = line.split('|');
                    return { value: (val ?? '').trim(), label: (lbl ?? '').trim() };
                  })
                  .filter((m) => m.value !== '' || m.label !== '');
                update({ metrics });
              }}
              onCommit={onCommit}
              rows={3}
            />
          </div>
        )}
      />
    </div>
  );
}

function LogoGridRenderer({ config }: RendererProps) {
  const v = config as unknown as CaseStudiesConfig;
  const cases = v.cases ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-6">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 items-center">
        {cases.map((c, i) => (
          <div
            key={i}
            className="aspect-[3/2] flex flex-col items-center justify-center rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-3"
          >
            {c.logoUrl ? (
              <img
                src={c.logoUrl}
                alt={c.companyName}
                className="max-h-10 object-contain mb-1"
              />
            ) : (
              <div className="text-sm font-semibold mb-1">{c.companyName}</div>
            )}
            {c.industry && (
              <div className="text-[10px] text-[var(--text-muted)]">
                {c.industry}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function CardWithMetricsRenderer({ config }: RendererProps) {
  const v = config as unknown as CaseStudiesConfig;
  const cases = v.cases ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {cases.map((c, i) => (
          <div
            key={i}
            className="rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] overflow-hidden"
          >
            {c.imageUrl && (
              <img
                src={c.imageUrl}
                alt={c.companyName}
                className="w-full aspect-[16/9] object-cover"
              />
            )}
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold">{c.companyName}</div>
                {c.industry && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--accent-subtle)] text-[var(--accent)]">
                    {c.industry}
                  </span>
                )}
              </div>
              {c.challenge && (
                <p className="text-xs text-[var(--text-muted)] mb-2">
                  <span className="font-semibold">課題:</span> {c.challenge}
                </p>
              )}
              {c.result && (
                <p className="text-sm mb-3">
                  <span className="font-semibold">結果:</span> {c.result}
                </p>
              )}
              {(c.metrics ?? []).length > 0 && (
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[var(--border)]">
                  {c.metrics!.map((m, j) => (
                    <div key={j} className="text-center">
                      <div className="text-lg font-bold text-[var(--accent)]">
                        {m.value}
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)]">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturedRenderer({ config }: RendererProps) {
  const v = config as unknown as CaseStudiesConfig;
  const c = (v.cases ?? [])[0];
  if (!c) {
    return (
      <section className="text-center text-[var(--text-muted)] py-8">
        導入事例を追加してください
      </section>
    );
  }
  return (
    <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2">
        {c.imageUrl ? (
          <img
            src={c.imageUrl}
            alt={c.companyName}
            className="w-full h-full object-cover aspect-[4/3] md:aspect-auto"
          />
        ) : (
          <div className="w-full bg-[var(--bg-hover)] aspect-[4/3] md:aspect-auto" />
        )}
        <div className="p-8 flex flex-col justify-center">
          {c.industry && (
            <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-[var(--accent-subtle)] text-[var(--accent)] mb-2 self-start">
              {c.industry}
            </span>
          )}
          <h4 className="text-xl font-bold mb-3">{c.companyName}</h4>
          {c.challenge && (
            <p className="text-sm text-[var(--text-secondary)] mb-2">
              <span className="font-semibold">課題:</span> {c.challenge}
            </p>
          )}
          {c.result && (
            <p className="text-sm mb-4">
              <span className="font-semibold">結果:</span> {c.result}
            </p>
          )}
          {(c.metrics ?? []).length > 0 && (
            <div className="flex flex-wrap gap-4 mt-auto">
              {c.metrics!.map((m, j) => (
                <div key={j}>
                  <div className="text-2xl font-bold text-[var(--accent)]">
                    {m.value}
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const sample = (): Record<string, unknown> => ({
  title: '導入事例',
  subtitle: '業界をリードする企業に選ばれています',
  cases: [
    {
      companyName: '株式会社サンプル',
      industry: '製造業',
      logoUrl: '',
      imageUrl: '',
      challenge: '在庫管理の属人化と棚卸の負担。',
      result: '導入 3 ヶ月で棚卸時間を 60% 削減。',
      metrics: [
        { value: '60%', label: '棚卸短縮' },
        { value: '40%', label: 'コスト削減' },
        { value: '12 名', label: 'ユーザー数' },
      ],
    },
    {
      companyName: 'テスト工業',
      industry: '物流',
      logoUrl: '',
      imageUrl: '',
      challenge: '受発注処理のリードタイム長期化。',
      result: '受注確定から出荷までを 1/3 に短縮。',
      metrics: [
        { value: '3x', label: '高速化' },
        { value: '99.9%', label: '正確性' },
        { value: '8 拠点', label: '展開' },
      ],
    },
  ] as CaseStudy[],
});

export const caseStudiesDefinition: SectionDefinition = {
  type: 'case_studies',
  label: '導入事例',
  icon: Briefcase,
  description: '導入企業の課題・結果・メトリクスを紹介。',
  defaultVariant: 'card_with_metrics',
  useCases: ['b2b_lease', 'service', 'platform'],
  variants: {
    logo_grid: {
      label: 'ロゴグリッド',
      description: '顧客ロゴを並べる',
      Renderer: LogoGridRenderer,
      Editor: CaseStudiesEditor,
      sample,
    },
    card_with_metrics: {
      label: 'メトリクスカード',
      description: '画像 + 業種 + 数値メトリクス',
      Renderer: CardWithMetricsRenderer,
      Editor: CaseStudiesEditor,
      sample,
    },
    featured: {
      label: 'フィーチャー',
      description: '1 件を大きく特集',
      Renderer: FeaturedRenderer,
      Editor: CaseStudiesEditor,
      sample,
    },
  },
};
